import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Mail, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AffiliateDisclosure } from '@/components/AffiliateFooter';

const FAQ_DATA = [
  {
    category: "Getting Started",
    questions: [
      {
        q: "What is Brand OS?",
        a: "Brand OS is your personal aesthetic optimization operating system. It combines seven key improvement categories — Debloat, Asymmetry, Coloring, Ratios, Physique, Styling, and Status — into one AI-powered platform that builds personalized daily routines, tracks your progress, and adapts to your journey."
      },
      {
        q: "How does the 3-day free trial work?",
        a: "When you create an account, you get full access to all features for 3 days — no credit card required upfront. After the trial ends, you'll need to choose a subscription plan to continue accessing your dashboard, tasks, and AI features."
      },
      {
        q: "What happens during onboarding?",
        a: "You'll go through 7 category steps where you paste your existing notes, plans, or goals. Our AI parses this information into structured routines and tasks. After that, you'll confirm suggested products, and Brand OS builds your personalized daily calendar."
      },
      {
        q: "Do I need to fill in all 7 categories?",
        a: "No — you can skip any category and come back to it later. However, the more categories you fill in, the smarter your daily tasks and cross-category insights become."
      },
    ]
  },
  {
    category: "Features & AI",
    questions: [
      {
        q: "How does the AI generate my daily tasks?",
        a: "Based on your onboarding data, Brand OS creates task templates for each category. These are scheduled throughout your day (morning, afternoon, evening) and adapt based on your notes, progress, and any changes you make."
      },
      {
        q: "What are cross-category insights?",
        a: "Brand OS connects information across all 7 categories. For example, if your Ratios data shows asymmetry, it might suggest specific exercises from your Physique plan, or if your Coloring data conflicts with Styling choices, you'll get a smart notification."
      },
      {
        q: "Can I add notes to change my tasks?",
        a: "Yes! The Notes section lets you add context like 'attending a party tonight' or 'feeling sick today.' The AI considers these notes when refreshing your tasks — not just for today but for future days too."
      },
      {
        q: "What is the Facial Harmony Score in Ratios?",
        a: "The Ratios category analyzes your facial proportions using photos you upload (front, left profile, right profile). It measures how close your features are to commonly studied harmony standards and tracks changes over time."
      },
    ]
  },
  {
    category: "Subscription & Billing",
    questions: [
      {
        q: "What are the pricing plans?",
        a: "We offer a monthly subscription and a discounted annual plan (one-time payment). Both give you full access to all features including AI-powered tasks, cross-category insights, progress tracking, and product recommendations."
      },
      {
        q: "Can I cancel anytime?",
        a: "Yes. Go to Profile Settings and scroll to the bottom — you'll see a 'Cancel my subscription' option. We also offer a pause option (3 months, no payment) if you want to take a break without losing your data and progress. Monthly subscriptions can be cancelled at any time. Quarterly and annual plans auto-renew but can be cancelled before the renewal date."
      },
      {
        q: "Do you offer refunds?",
        a: "Please contact support@buildbrandos.com within 7 days of purchase if you're unsatisfied. We review refund requests on a case-by-case basis."
      },
    ]
  },
  {
    category: "Privacy & Data",
    questions: [
      {
        q: "Is my facial data safe?",
        a: "Your photos and personal data are encrypted and stored securely. We never share your images or personal information with third parties. You can delete your account and all associated data at any time."
      },
      {
        q: "Do you sell my data?",
        a: "Absolutely not. Brand OS never sells, rents, or shares your personal data. We use your information solely to provide and improve your personalized experience."
      },
      {
        q: "Are you GDPR/CCPA compliant?",
        a: "Yes. We comply with GDPR (EU), CCPA (California), PIPEDA (Canada), and CAN-SPAM (USA). You have the right to access, modify, or delete your personal data at any time."
      },
    ]
  },
  {
    category: "Products & Affiliate Links",
    questions: [
      {
        q: "Why does Brand OS suggest products?",
        a: "Product suggestions are based on your specific improvement plan. If your AI-generated tasks involve a particular supplement, skincare product, or tool, we link to it so you can easily find what you need."
      },
      {
        q: "Are the product links affiliate links?",
        a: "Some product links are Amazon affiliate links. This means Brand OS may earn a small commission if you purchase through them, at no extra cost to you. This helps support the platform."
      },
      {
        q: "Do I have to buy suggested products?",
        a: "No. During onboarding, you can mark products as 'I have this,' 'I have something similar,' or 'I need this.' Tasks automatically adapt — if you have a similar product, the task will note to use your own item."
      },
    ]
  },
];

export default function FAQPage() {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState({});

  const toggle = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-purple-200 rounded-xl flex items-center justify-center shadow-inner">
              <span className="text-white font-bold text-lg font-['Manrope']">B</span>
            </div>
            <span className="text-lg font-semibold tracking-tight font-['Manrope']">Brand OS</span>
          </div>
          <Button onClick={() => navigate('/auth')} className="rounded-full h-10 px-6 bg-[#09090b] text-white hover:opacity-90 font-medium text-sm" data-testid="faq-get-started-btn">
            Get Started
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-8" data-testid="faq-back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back
        </button>

        <div className="flex items-center gap-3 mb-4">
          <HelpCircle className="w-6 h-6 text-purple-500" strokeWidth={1.5} />
          <h1 className="text-4xl font-bold tracking-tight" data-testid="faq-title">
            Frequently Asked Questions
          </h1>
        </div>
        <p className="text-gray-400 mb-12 text-lg">Everything you need to know about Brand OS.</p>

        <div className="space-y-10">
          {FAQ_DATA.map((section) => (
            <div key={section.category}>
              <h2 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-4" data-testid={`faq-section-${section.category.toLowerCase().replace(/\s/g, '-')}`}>
                {section.category}
              </h2>
              <div className="space-y-2">
                {section.questions.map((item, idx) => {
                  const key = `${section.category}-${idx}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={key} className="border border-gray-100 rounded-2xl overflow-hidden" data-testid={`faq-item-${key}`}>
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50"
                        style={{ transition: 'background-color 0.2s' }}
                        data-testid={`faq-toggle-${key}`}
                      >
                        <span className="text-sm font-semibold text-gray-800 pr-4">{item.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={1.5} />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={1.5} />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 pt-0 animate-fade-in-up">
                          <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gray-50 rounded-3xl p-10">
          <h3 className="text-xl font-bold mb-2">Still have questions?</h3>
          <p className="text-gray-400 mb-6">We're here to help you on your personal brand journey.</p>
          <a
            href="mailto:support@buildbrandos.com"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#09090b] text-white font-medium text-sm hover:opacity-90"
            style={{ transition: 'opacity 0.2s' }}
            data-testid="faq-contact-btn"
          >
            <Mail className="w-4 h-4" strokeWidth={1.5} />
            support@buildbrandos.com
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-purple-200 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs font-['Manrope']">B</span>
            </div>
            <span className="font-medium text-sm text-gray-400">Brand OS</span>
          </div>
          <nav className="flex flex-wrap gap-6 text-sm text-gray-400">
            <button onClick={() => navigate('/pricing')} className="hover:text-gray-700 transition-colors">Pricing</button>
            <button onClick={() => navigate('/blog')} className="hover:text-gray-700 transition-colors">Blog</button>
            <button onClick={() => navigate('/privacy')} className="hover:text-gray-700 transition-colors">Privacy Policy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-gray-700 transition-colors">Terms of Service</button>
          </nav>
        </div>
        <div className="max-w-4xl mx-auto mt-4">
          <AffiliateDisclosure />
        </div>
      </footer>
    </div>
  );
}
